import { useEffect, type RefObject } from 'react';

export type GalleryPointerDragSession = {
  activePointerId: number | null;
  activePointerType: string | null;
  dragStartPos: { x: number; y: number } | null;
  hasDragged: boolean;
};

export function createInitialGalleryPointerDragSession(): GalleryPointerDragSession {
  return {
    activePointerId: null,
    activePointerType: null,
    dragStartPos: null,
    hasDragged: false,
  };
}

/**
 * Single window-level pointer listeners for the whole gallery track (Vercel:
 * dedupe global listeners). Cells mutate `sessionRef` on pointer down/up.
 */
export function useGalleryPointerWindowListeners(
  sessionRef: RefObject<GalleryPointerDragSession>
): void {
  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      const s = sessionRef.current;
      if (!s || s.activePointerId !== event.pointerId || !s.dragStartPos) {
        return;
      }

      const dx = event.clientX - s.dragStartPos.x;
      const dy = event.clientY - s.dragStartPos.y;
      if (Math.hypot(dx, dy) > 5) {
        s.hasDragged = true;
      }
    };

    const onPointerUpOrCancel = (event: PointerEvent) => {
      const s = sessionRef.current;
      if (!s || s.activePointerId !== event.pointerId) {
        return;
      }

      s.activePointerId = null;
      s.activePointerType = null;
      s.dragStartPos = null;
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerup', onPointerUpOrCancel);
    window.addEventListener('pointercancel', onPointerUpOrCancel);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUpOrCancel);
      window.removeEventListener('pointercancel', onPointerUpOrCancel);
    };
  }, [sessionRef]);
}
